// filepath: apps/web/src/features/admin/recordPermissions/queries.ts
//
// FR-UI-ADM-2 — TanStack Query hooks for the record-level +
// field-level permission editor.
//
// All hooks include `tenantSlug` as the 2nd segment of the cache
// key (INV-1 — mirrors adminKeys.matrix and
// adminKeys.fieldVisibility in queries.ts).
//
// Mutations carry `If-Match: <version>` (INV-7 — optimistic
// concurrency on the role's version column). The server returns
// 409 ROLE_VERSION_MISMATCH envelope with `current_version` on
// stale writes; the editor surfaces this via
// RecordPermissionConflictDialog (mirrors FR-UI-CORE-6).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiError } from "@/definitions/types";
import { adminKeys } from "@/features/admin/queries";
import { apiFetch } from "@/api";
import type {
  EntityFieldOwnerResponse,
  FieldPermissionGrant,
  FieldPermissionsReadResponse,
  RecordRuleResponse,
} from "./types";

// ISS-377 round 2.1 (R2-B2): the six raw `fetch('/api/...')` calls
// below are migrated to the canonical `apiFetch` client. The
// previous shape attached NO `Authorization: Bearer <access_token>`
// header (the Go server's `internal/auth.AuthMiddleware` requires
// the bearer at `auth.go:51-62`); every call silently 401'd in
// production. Routing through `apiFetch` attaches the bearer via
// the registered OIDC accessor and runs the 401 → silent-refresh
// → retry chain. The static-guard forbidlist
// (`apps/web/src/test/noRawApiFetchForbidlist.ts`) now requires
// every /api call in apps/web/src/** to go through this client.

/** Cache keys for the record/field permission editor (FR-UI-ADM-2).
 *  tenantSlug is the 2nd segment (INV-1). */
export const recordPermissionKeys = {
  recordRule: (tenantSlug: string, roleName: string, entityType: string) =>
    ["admin", tenantSlug, "record-rule", roleName, entityType] as const,
  fieldPermissions: (
    tenantSlug: string,
    roleName: string,
    entityType: string,
  ) =>
    ["admin", tenantSlug, "field-permissions", roleName, entityType] as const,
  entityFieldOwner: (tenantSlug: string, entityType: string) =>
    ["admin", tenantSlug, "entity-field-owner", entityType] as const,
};

/**
 * useRecordRuleQuery — GET /api/admin/roles/{roleName}/record-rule/{entityType}
 * → RecordRuleResponse. Returns the ownership + condition rules
 * for the (role, entity) pair. Both fields are nullable when no
 * rule of that kind is configured yet.
 */
export function useRecordRuleQuery(roleName: string, entityType: string) {
  const { tenantSlug } = useTenant();
  return useQuery<RecordRuleResponse, ApiError>({
    queryKey: recordPermissionKeys.recordRule(tenantSlug, roleName, entityType),
    queryFn: async () => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch.
      const r = await apiFetch(
        `/api/admin/roles/${encodeURIComponent(roleName)}/record-rule/${encodeURIComponent(entityType)}`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as RecordRuleResponse;
    },
    enabled: Boolean(roleName) && Boolean(entityType),
  });
}

/**
 * useFieldPermissionsQuery — GET /api/admin/roles/{roleName}/field-permissions/{entityType}
 * → FieldPermissionsReadResponse. Returns the per-field grants
 * for the (role, entity) pair. Hidden fields are NOT in the
 * `grants` array (server row absence semantics).
 */
export function useFieldPermissionsQuery(roleName: string, entityType: string) {
  const { tenantSlug } = useTenant();
  return useQuery<FieldPermissionsReadResponse, ApiError>({
    queryKey: recordPermissionKeys.fieldPermissions(
      tenantSlug,
      roleName,
      entityType,
    ),
    queryFn: async () => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch.
      const r = await apiFetch(
        `/api/admin/roles/${encodeURIComponent(roleName)}/field-permissions/${encodeURIComponent(entityType)}`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as FieldPermissionsReadResponse;
    },
    enabled: Boolean(roleName) && Boolean(entityType),
  });
}

/**
 * useEntityFieldOwnerQuery — GET /api/admin/entity-field-owners/{entityType}
 * → EntityFieldOwnerResponse.
 */
export function useEntityFieldOwnerQuery(entityType: string) {
  const { tenantSlug } = useTenant();
  return useQuery<EntityFieldOwnerResponse, ApiError>({
    queryKey: recordPermissionKeys.entityFieldOwner(tenantSlug, entityType),
    queryFn: async () => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch.
      const r = await apiFetch(
        `/api/admin/entity-field-owners/${encodeURIComponent(entityType)}`,
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as EntityFieldOwnerResponse;
    },
    enabled: Boolean(entityType),
  });
}

/**
 * UpsertRecordRuleRequest — body for
 * PUT /api/admin/roles/{roleName}/record-rule/{entityType}.
 */
export type UpsertRecordRuleRequest = {
  rule_kind: "ownership" | "condition";
  rule_expr: string;
};

/**
 * useRecordRuleMutation — PUT record-rule (AC-2 save flow).
 * Carries `If-Match: <version>` per INV-7. On 409 the server
 * returns the current_version; the editor surfaces this via
 * RecordPermissionConflictDialog.
 */
export function useRecordRuleMutation(roleName: string, entityType: string) {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    { version: number },
    ApiError,
    UpsertRecordRuleRequest & { version: number }
  >({
    mutationFn: async (body) => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch. The
      // `If-Match` header (INV-7) is forwarded through `init.headers`
      // and survives the apiFetch header merge (client-supplied
      // Authorization / X-Idempotency-Key are appended AFTER the
      // caller's headers so the caller's `If-Match` is preserved).
      const r = await apiFetch(
        `/api/admin/roles/${encodeURIComponent(roleName)}/record-rule/${encodeURIComponent(entityType)}`,
        {
          method: "PUT",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": String(body.version),
            },
            body: JSON.stringify({
              rule_kind: body.rule_kind,
              rule_expr: body.rule_expr,
            }),
          },
          idempotencyKey: true,
        },
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as { version: number };
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: recordPermissionKeys.recordRule(
          tenantSlug,
          roleName,
          entityType,
        ),
      });
    },
  });
}

/**
 * UpsertFieldPermissionsRequest — body for
 * PUT /api/admin/roles/{roleName}/field-permissions/{entityType}.
 *
 * The `grants` array is the FULL set of field grants; hidden
 * fields are OMITTED (server deletes the row per FR-AUTH-6
 * full-set-replace semantics).
 */
export type UpsertFieldPermissionsRequest = {
  grants: FieldPermissionGrant[];
  version: number;
};

/**
 * useFieldPermissionsMutation — PUT field-permissions (AC-3 save flow).
 */
export function useFieldPermissionsMutation(
  roleName: string,
  entityType: string,
) {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    { version: number; grants: FieldPermissionGrant[] },
    ApiError,
    UpsertFieldPermissionsRequest
  >({
    mutationFn: async (body) => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch.
      const r = await apiFetch(
        `/api/admin/roles/${encodeURIComponent(roleName)}/field-permissions/${encodeURIComponent(entityType)}`,
        {
          method: "PUT",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": String(body.version),
            },
            body: JSON.stringify({ grants: body.grants }),
          },
          idempotencyKey: true,
        },
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as {
        version: number;
        grants: FieldPermissionGrant[];
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: recordPermissionKeys.fieldPermissions(
          tenantSlug,
          roleName,
          entityType,
        ),
      });
    },
  });
}

/**
 * UpsertEntityFieldOwnerRequest — body for
 * PUT /api/admin/entity-field-owners/{entityType}.
 */
export type UpsertEntityFieldOwnerRequest = {
  owner_field: string;
  version: number;
};

/**
 * useEntityFieldOwnerMutation — PUT entity-field-owner (AC-1 save flow).
 */
export function useEntityFieldOwnerMutation(entityType: string) {
  const { tenantSlug } = useTenant();
  const qc = useQueryClient();
  return useMutation<
    { version: number; owner_field: string },
    ApiError,
    UpsertEntityFieldOwnerRequest
  >({
    mutationFn: async (body) => {
      // ISS-377 round 2.1 (R2-B2): migrated to apiFetch.
      const r = await apiFetch(
        `/api/admin/entity-field-owners/${encodeURIComponent(entityType)}`,
        {
          method: "PUT",
          init: {
            headers: {
              "Content-Type": "application/json",
              "If-Match": String(body.version),
            },
            body: JSON.stringify({ owner_field: body.owner_field }),
          },
          idempotencyKey: true,
        },
      );
      if (!r.ok) {
        throw new ApiError(r.statusText, r.status, `http_${r.status}`);
      }
      return (await r.json()) as { version: number; owner_field: string };
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: recordPermissionKeys.entityFieldOwner(tenantSlug, entityType),
      });
    },
  });
}

/**
 * classifyRecordPermissionError — maps the raw fetch error to the
 * typed envelope the page consumes. Mirrors
 * classifyRoleFieldVisibilityError from useRoleFieldVisibility.ts.
 */
export function classifyRecordPermissionError(
  err: Error | ApiError | unknown,
): import("./types").RecordPermissionFetchError {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return {
        kind: "permission_denied",
        message: err.message,
        retriable: false,
      };
    }
    if (err.status === 410 || err.status === 404) {
      return {
        kind: "stale_version",
        message: err.message,
        retriable: true,
      };
    }
    if (err.status === 429) {
      return {
        kind: "rate_limit",
        message: err.message,
        retriable: true,
      };
    }
    if (err.status === 0) {
      return {
        kind: "network",
        message: err.message,
        retriable: true,
      };
    }
  }
  return {
    kind: "fetch_failure",
    message: err instanceof Error ? err.message : String(err),
    retriable: false,
  };
}

// Re-export adminKeys so consumers of the editor don't need to
// import from two different files. The matrix + field-visibility
// sub-modules share the adminKeys factory in queries.ts.
export { adminKeys };
